import { fighterEntryDisplayMapping, weightClasses } from "../constants";
import { getEventFighterWeightClass, isMinorAtDate } from "./common";
import { isMmaPair, resolveCorners } from "./judge-scoring";

/**
 * The fight card export (Matched tab → Export): the running order as an
 * Excel sheet laid out like the card MFL prints, red corner in red, blue in
 * blue, one section per run of matches in the same discipline, and the match
 * number in yellow when either fighter is 17 or younger on the event day.
 *
 * Excel, not CSV: a CSV file can't carry colours or merged cells. Admins
 * download it and finish it off in Excel (time slots, last-minute changes),
 * so the sheet is plain cells and fills, nothing locked or computed.
 *
 * buildFightCard is pure (tested in fight-card-export.test.js);
 * writeFightCardWorkbook takes ExcelJS as an argument so the page can load
 * the library only when someone exports.
 */

export const FIGHT_CARD_COLORS = Object.freeze({
    red: "FFFF0000",
    blue: "FF4A86E8",
    minor: "FFFFFF00",
    border: "FF000000",
});

const disciplineLabel = (entry) =>
    entry ? (fighterEntryDisplayMapping[entry] || entry).toUpperCase() : "OTHER";

const weightClassLabel = (code) => (code ? weightClasses[code] || code : "");

function cornerRow(eventFighter, eventDate) {
    const fighter = eventFighter?.fighter || {};
    return {
        fighterId: eventFighter?.fighterId || null,
        name: String(fighter.name || "").trim(),
        gym: String(fighter.gymNameOrTeamName || "").trim(),
        minor: isMinorAtDate(fighter.birthdate, eventDate),
        weightClass: getEventFighterWeightClass(eventFighter),
    };
}

/**
 * The card, in running order, split into sections.
 *
 * @param {Array<{pairKey: string, pair: {fighterA, fighterB}, originalIndex: number}>} entries
 *        every matched bout in running order (the Matched tab's pairEntries)
 * @param {{dateTime?, boutCorners?: Object<string,string>}} event
 * @returns {{sections: Array<{key, discipline, label, matches: Array}>, skipped: number}}
 *   Pulled-out matches (and any pair missing a fighter) are left out, and
 *   counted in `skipped`. Match numbers are the ones on the Matched tab and the
 *   judge page, so a left-out match leaves a gap.
 */
export function buildFightCard(entries, event) {
    const sections = [];
    let skipped = 0;

    (entries || []).forEach((entry) => {
        const a = entry?.pair?.fighterA;
        const b = entry?.pair?.fighterB;
        if (!a || !b || a.isPulledOut || b.isPulledOut) {
            skipped += 1;
            return;
        }
        const { red, blue } = resolveCorners(a, b, event?.boutCorners?.[entry.pairKey]);
        const discipline = isMmaPair(red, blue) ? "MMA" : red?.entry || blue?.entry || null;
        const redRow = cornerRow(red, event?.dateTime);
        const blueRow = cornerRow(blue, event?.dateTime);
        const crossClass =
            !!redRow.weightClass && !!blueRow.weightClass && redRow.weightClass !== blueRow.weightClass;
        const weightClass = crossClass
            ? `${weightClassLabel(redRow.weightClass)} / ${weightClassLabel(blueRow.weightClass)}`
            : weightClassLabel(redRow.weightClass || blueRow.weightClass);
        const match = {
            pairKey: entry.pairKey,
            number: (entry.originalIndex ?? 0) + 1,
            red: redRow,
            blue: blueRow,
            weightClass,
            crossClass,
            minor: redRow.minor || blueRow.minor,
        };

        const last = sections[sections.length - 1];
        if (last && last.discipline === discipline) {
            last.matches.push(match);
        } else {
            sections.push({
                key: `${discipline || "OTHER"}@${match.number}`,
                discipline,
                label: disciplineLabel(discipline),
                matches: [match],
            });
        }
    });

    return { sections, skipped };
}

/** "MFL 12: Kuala Lumpur - fight card.xlsx", safe on every OS. */
export function fightCardFileName(eventName) {
    const base = String(eventName || "")
        // Characters Windows or macOS won't take in a file name.
        // eslint-disable-next-line no-control-regex
        .replace(/[\\/:*?"<>|\u0000-\u001f]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    return `${base || "Event"} - fight card.xlsx`;
}

/**
 * The card as an ExcelJS workbook, laid out like the printed card:
 *
 *   A: number  B: red name  C: red gym  D: "vs"  E: blue name  F: blue gym  G: weight class
 *
 * Each section starts with a header row: the discipline across B–F, and
 * "Weight Class" in G. Header rows are two lines tall, as on the printed
 * card, so a time slot typed in on a second line (Alt+Enter) shows in full:
 * Excel doesn't grow a row to fit a merged cell.
 *
 * @param {object} ExcelJS            the exceljs module
 * @param {{sections: Array}} card    from buildFightCard
 * @param {{sheetName?: string}} [opts]
 */
export function writeFightCardWorkbook(ExcelJS, card, { sheetName = "Fight card" } = {}) {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = "Malaysia Fight League";
    const sheet = workbook.addWorksheet(sheetName.slice(0, 31) || "Fight card", {
        pageSetup: {
            orientation: "landscape",
            paperSize: 9, // A4
            fitToPage: true,
            fitToWidth: 1,
            fitToHeight: 0,
            horizontalCentered: true,
        },
    });
    sheet.columns = [
        { width: 8 },
        { width: 36 },
        { width: 28 },
        { width: 8 },
        { width: 36 },
        { width: 30 },
        { width: 36 },
    ];

    const thin = { style: "thin", color: { argb: FIGHT_CARD_COLORS.border } };
    const border = { top: thin, left: thin, bottom: thin, right: thin };
    const fill = (argb) => ({ type: "pattern", pattern: "solid", fgColor: { argb } });
    const font = { name: "Arial", size: 11, bold: true, color: { argb: "FF000000" } };
    const center = { horizontal: "center", vertical: "middle", wrapText: true };

    const style = (cell, extra = {}) => {
        cell.font = font;
        cell.border = border;
        cell.alignment = { ...center, ...(extra.alignment || {}) };
        if (extra.fill) cell.fill = fill(extra.fill);
    };

    let rowNo = 1;
    card.sections.forEach((section) => {
        const header = sheet.getRow(rowNo);
        header.getCell(2).value = section.label;
        header.getCell(7).value = "Weight Class";
        for (let c = 1; c <= 7; c += 1) style(header.getCell(c));
        sheet.mergeCells(rowNo, 2, rowNo, 6);
        header.height = 32;
        rowNo += 1;

        const first = rowNo;
        section.matches.forEach((match) => {
            const row = sheet.getRow(rowNo);
            row.getCell(1).value = match.number;
            row.getCell(2).value = match.red.name;
            row.getCell(3).value = match.red.gym;
            row.getCell(5).value = match.blue.name;
            row.getCell(6).value = match.blue.gym;
            row.getCell(7).value = match.weightClass;
            style(row.getCell(1), match.minor ? { fill: FIGHT_CARD_COLORS.minor } : {});
            style(row.getCell(2), { fill: FIGHT_CARD_COLORS.red });
            style(row.getCell(3), { fill: FIGHT_CARD_COLORS.red });
            style(row.getCell(4));
            style(row.getCell(5), { fill: FIGHT_CARD_COLORS.blue });
            style(row.getCell(6), { fill: FIGHT_CARD_COLORS.blue });
            style(row.getCell(7));
            // Two weight classes wrap onto a second line.
            row.height = match.crossClass ? 32 : 20;
            rowNo += 1;
        });
        // One "vs" per section, down the middle.
        sheet.getCell(first, 4).value = "vs";
        if (rowNo - 1 > first) sheet.mergeCells(first, 4, rowNo - 1, 4);
        sheet.getCell(first, 4).alignment = { ...center, vertical: "top" };
    });

    return workbook;
}
